import { type FC, useContext } from "react";
import type { OverlayTriggerState } from "react-stately";
import { DialogComponent, HStack, LoadingSpinner, Modal, TriggerButton, TriggerButtonSize, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import { GenomePanel } from "./GenomePanel.tsx";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";
import { TrackConfigurationProvider } from "./TrackConfigurationProvider.tsx";
import { TrackPanel } from "./TrackPanel.tsx";
import style from "./TracksConfiguration.module.css";

export const TracksConfigurationDialog = () => {
  const { overlayProps, state } = useOverlay({});

  return (
    <>
      <TriggerButton
        size={TriggerButtonSize.Normal}
        icon={"layers"}
        onPress={() => {
          state.open();
        }}
        label={"Tracks"}
        aria-label="Track Configuration"
        showLabel
      />
      <Modal state={state} {...overlayProps}>
        <TrackConfigurationProvider isOpen={state.isOpen}>
          <ConfigDialogContent state={state} />
        </TrackConfigurationProvider>
      </Modal>
    </>
  );
};

interface DialogContentProps {
  state: OverlayTriggerState;
}

const ConfigDialogContent: FC<DialogContentProps> = ({ state }) => {
  const { trackConfiguration, applyChanges, isLoading } = useContext(TrackConfigurationContext);

  return (
    <DialogComponent
      data-testid="track-configuration"
      contentStyle={{ padding: 0, overflowY: "hidden" }}
      mainButton={{
        label: "Apply",
        onPress: () => {
          applyChanges(trackConfiguration);
          state.close();
        },
      }}
      asPanelHeader
      closeFn={() => {
        state.close();
      }}
      title="Track Configuration"
    >
      <VStack spacing={0}>
        <HStack spacing={0} className={style.trackConfiguration}>
          <GenomePanel />
          {isLoading ? (
            <VStack style={{ minWidth: 400, justifyContent: "center", alignItems: "center" }}>
              <LoadingSpinner />
            </VStack>
          ) : (
            <TrackPanel />
          )}
        </HStack>
      </VStack>
    </DialogComponent>
  );
};
